import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  TextField, 
  Button,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  alpha,
  Collapse,
  Fade,
  Menu,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NumbersIcon from '@mui/icons-material/Numbers';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import PasswordIcon from '@mui/icons-material/Password';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckIcon from '@mui/icons-material/Check';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { db } from '../firebase';
import { doc, onSnapshot, setDoc, getDoc, updateDoc, arrayUnion, collection, getDocs, writeBatch } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import CategoryPreview from './CategoryPreview';
import { useNavigate } from 'react-router-dom';
import CategoryBulkUpdate from './CategoryBulkUpdate';
import CategoryCSVImport from './CategoryCSVImport';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FilterListIcon from '@mui/icons-material/FilterList';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BarChartIcon from '@mui/icons-material/BarChart';
import StarIcon from '@mui/icons-material/Star';
import FileUploadIcon from '@mui/icons-material/FileUpload';

const dataTypes = [
  { value: 'text', label: 'Text', icon: TextFieldsIcon },
  { value: 'number', label: 'Number', icon: NumbersIcon },
  { value: 'url', label: 'URL', icon: LinkIcon },
  { value: 'email', label: 'Email', icon: EmailIcon },
  { value: 'password', label: 'Password', icon: PasswordIcon },
  { value: 'date', label: 'Date', icon: CalendarTodayIcon },
  { value: 'telephone', label: 'Phone', icon: PhoneIcon },
  { value: 'checkbox', label: 'Checkbox', icon: CheckBoxIcon },
];

const CategoryManager = ({ onCategoriesChange }) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemType, setNewItemType] = useState('text');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editedGroupTitle, setEditedGroupTitle] = useState('');
  const [editedItemTitle, setEditedItemTitle] = useState('');
  const [editedItemType, setEditedItemType] = useState('text');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [featureMenuAnchor, setFeatureMenuAnchor] = useState(null);

  // Subscribe to real-time updates
  useEffect(() => {
    let unsubscribe = null;

    const initializeFirestore = async () => {
      try {
        // First check if user exists and has admin role
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists() || userDoc.data().role !== 'admin') {
          setError('You do not have permission to access this feature.');
          setLoading(false);
          return;
        }

        const categoriesRef = doc(db, 'categories', 'active_categories');
        
        // Set up real-time listener
        unsubscribe = onSnapshot(
          categoriesRef,
          { includeMetadataChanges: false },  // Only listen for actual data changes, not metadata
          (doc) => {
            if (doc.exists()) {
              const data = doc.data();
              console.log('Raw data from database:', data.groups);
              
              // Ensure all IDs are strings and add prefixes
              const processedGroups = (data.groups || []).map(group => {
                console.log('Processing group:', group.title, 'with items:', group.items);
                return {
                  ...group,
                  id: String(group.id),
                  items: group.items.map(item => {
                    console.log('Processing item:', item.title, 'with ID:', item.id);
                    return {
                      ...item,
                      id: `draggable-${String(item.id)}`
                    };
                  })
                };
              });
              
              console.log('Processed groups:', processedGroups);
              setCategories(processedGroups);
              
              // Initialize expanded state for groups
              const initialExpanded = {};
              processedGroups.forEach(group => {
                initialExpanded[group.id] = true;
              });
              setExpandedGroups(initialExpanded);
              
              if (onCategoriesChange) {
                onCategoriesChange(processedGroups);
              }
            }
            setLoading(false);
          },
          (error) => {
            console.error('Error loading categories:', error);
            setError(error.message);
            setSnackbar({
              open: true,
              message: `Failed to load categories: ${error.message}`,
              severity: 'error',
            });
            setLoading(false);
          }
        );
        
        console.log('CategoryManager: Real-time listener set up');
      } catch (error) {
        console.error('Error initializing Firestore:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    if (user) {
      initializeFirestore();
    } else {
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        console.log('CategoryManager: Unsubscribing listener');
        unsubscribe();
      }
    };
  }, [user, onCategoriesChange]);

  // Helper function to normalize category names
  const normalizeId = (name) => {
    return `category_${name.toLowerCase()
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '_')}`;
  };

  // Helper function to normalize group names
  const normalizeGroupId = (name) => {
    return `group_${name.toLowerCase()
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '_')}`;
  };

  // Helper function to check for duplicate names
  const isDuplicateName = (name, type, currentId = null) => {
    if (type === 'group') {
      return categories.some(group => 
        group.title.toLowerCase() === name.toLowerCase() && group.id !== currentId
      );
    } else {
      return categories.some(group => 
        group.items.some(item => 
          item.title.toLowerCase() === name.toLowerCase() && item.id !== currentId
        )
      );
    }
  };

  const addHistoryEntry = async (action, changes) => {
    try {
      const historyRef = doc(db, 'categories', 'history');
      const historyDoc = await getDoc(historyRef);
      
      const newEntry = {
        timestamp: new Date(),
        action,
        changes,
        performedBy: user.email
      };

      if (historyDoc.exists()) {
        await updateDoc(historyRef, {
          entries: arrayUnion(newEntry)
        });
      } else {
        await setDoc(historyRef, {
          entries: [newEntry]
        });
      }
    } catch (error) {
      console.error('Error updating history:', error);
    }
  };

  const moveItem = (groupId, currentIndex, direction) => {
    const newCategories = [...categories];
    const groupIndex = newCategories.findIndex(g => g.id === groupId);
    if (groupIndex === -1) return;

    const items = [...newCategories[groupIndex].items];
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= items.length) return;

    // Swap items
    [items[currentIndex], items[newIndex]] = [items[newIndex], items[currentIndex]];

    // Update order values
    items.forEach((item, index) => {
      item.order = index;
    });

    newCategories[groupIndex] = {
      ...newCategories[groupIndex],
      items
    };

    setCategories(newCategories);
    setHasUnsavedChanges(true);
    setSnackbar({
      open: true,
      message: 'Changes made. Remember to save!',
      severity: 'info'
    });
  };

  const moveItemToGroup = (sourceGroupId, targetGroupId, itemIndex) => {
    const newCategories = [...categories];
    const sourceGroupIndex = newCategories.findIndex(g => g.id === sourceGroupId);
    const targetGroupIndex = newCategories.findIndex(g => g.id === targetGroupId);
    
    if (sourceGroupIndex === -1 || targetGroupIndex === -1) return;

    const sourceItems = [...newCategories[sourceGroupIndex].items];
    const targetItems = [...newCategories[targetGroupIndex].items];
    
    // Move item from source to target
    const [movedItem] = sourceItems.splice(itemIndex, 1);
    targetItems.push(movedItem);

    // Update order values
    sourceItems.forEach((item, index) => {
      item.order = index;
    });
    targetItems.forEach((item, index) => {
      item.order = index;
    });

    newCategories[sourceGroupIndex] = {
      ...newCategories[sourceGroupIndex],
      items: sourceItems
    };
    newCategories[targetGroupIndex] = {
      ...newCategories[targetGroupIndex],
      items: targetItems
    };

    setCategories(newCategories);
    setHasUnsavedChanges(true);
    setSnackbar({
      open: true,
      message: 'Changes made. Remember to save!',
      severity: 'info'
    });
  };

  const addGroup = async () => {
    if (!newGroupTitle) return;
    
    // Check for duplicate names
    if (isDuplicateName(newGroupTitle, 'group')) {
      setSnackbar({
        open: true,
        message: 'A group with this name already exists',
        severity: 'error'
      });
      return;
    }

    const newGroup = {
      id: normalizeGroupId(newGroupTitle),
      title: newGroupTitle,
      items: [],
      order: categories.length
    };

    const newCategories = [...categories, newGroup];
    setCategories(newCategories);
    setExpandedGroups(prev => ({ ...prev, [newGroup.id]: true }));
    setNewGroupTitle('');

    await saveCategories(newCategories);
    await addHistoryEntry('create_group', {
      groupId: newGroup.id,
      title: newGroup.title
    });
  };

  const addItem = async () => {
    if (!newItemTitle || !selectedGroup) return;

    // Check for duplicate names
    if (isDuplicateName(newItemTitle, 'item')) {
      setSnackbar({
        open: true,
        message: 'A category with this name already exists',
        severity: 'error'
      });
      return;
    }

    const newCategories = categories.map(group => {
      if (group.id === selectedGroup) {
        const newItem = {
          id: normalizeId(newItemTitle),
          title: newItemTitle,
          type: newItemType,
          isRequired: false,
          isArchived: false,
          isCopyable: false,
          order: group.items.length,
          fieldName: normalizeId(newItemTitle).replace('category_', '')
        };

        return {
          ...group,
          items: [...group.items, newItem]
        };
      }
      return group;
    });

    setCategories(newCategories);
    setNewItemTitle('');
    setNewItemType('text');
    
    await saveCategories(newCategories);
    await addHistoryEntry('create_category', {
      categoryId: normalizeId(newItemTitle),
      groupId: selectedGroup,
      title: newItemTitle,
      type: newItemType
    });
  };

  const deleteItem = async (groupId, itemId) => {
    setConfirmAction({
      type: 'delete_item',
      data: { groupId, itemId },
      message: 'Are you sure you want to archive this category? This will hide it from all hotels but preserve existing data.'
    });
    setConfirmDialogOpen(true);
  };

  const deleteGroup = async (groupId) => {
    setConfirmAction({
      type: 'delete_group',
      data: { groupId },
      message: 'Are you sure you want to delete this group? All categories in this group must be empty or archived first.'
    });
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      switch (confirmAction.type) {
        case 'delete_item': {
          const { groupId, itemId } = confirmAction.data;
          const newCategories = categories.map(group => {
            if (group.id === groupId) {
              return {
                ...group,
                items: group.items.map(item => 
                  item.id === itemId 
                    ? { ...item, isArchived: true }
                    : item
                )
              };
            }
            return group;
          });
          
          setCategories(newCategories);
          await saveCategories(newCategories);
          await addHistoryEntry('archive_category', {
            categoryId: itemId,
            groupId
          });
          break;
        }
        case 'delete_group': {
          const { groupId } = confirmAction.data;
          const group = categories.find(g => g.id === groupId);
          
          // Check if group has non-archived items
          if (group.items.some(item => !item.isArchived)) {
            setSnackbar({
              open: true,
              message: 'Cannot delete group with active categories. Archive all categories first.',
              severity: 'error'
            });
            return;
          }

          const newCategories = categories.filter(g => g.id !== groupId);
          // Update order numbers
          newCategories.forEach((group, index) => {
            group.order = index;
          });

          setCategories(newCategories);
          setExpandedGroups(prev => {
            const newExpanded = { ...prev };
            delete newExpanded[groupId];
            return newExpanded;
          });

          await saveCategories(newCategories);
          await addHistoryEntry('delete_group', {
            groupId,
            title: group.title
          });
          break;
        }
        default: {
          console.warn('Unknown action type:', confirmAction.type);
          break;
        }
      }
    } catch (error) {
      console.error('Error handling action:', error);
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setConfirmDialogOpen(false);
      setConfirmAction(null);
    }
  };

  const saveCategories = async (categoriesToSave) => {
    // Check if user exists and is authenticated
    if (!user?.uid) {
      setSnackbar({
        open: true,
        message: 'You must be logged in to save categories',
        severity: 'error',
      });
      return;
    }

    // Check admin role
    if (user.role !== 'admin') {
      setSnackbar({
        open: true,
        message: 'Only admin users can save categories',
        severity: 'error',
      });
      return;
    }
    
    // WARNING: Check if there's an item currently being edited
    if (editingItem) {
      setSnackbar({
        open: true,
        message: 'Please save or cancel the item you are editing first before saving all changes',
        severity: 'warning',
      });
      return;
    }
    
    try {
      setSaving(true);
      console.log('Starting save operation with user:', user.email);

      // Process categories to ensure clean data structure
      const processedCategories = categoriesToSave.map(group => {
        console.log('Processing group:', group.title);
        return {
          id: String(group.id).replace('droppable-', '').replace('draggable-', ''),
          title: String(group.title),
          order: Number(group.order) || 0,
          items: group.items.map(item => {
            console.log('Processing item:', item.title);
            return {
              id: String(item.id).replace('draggable-', ''),
              title: String(item.title),
              type: String(item.type || 'text'),
              isRequired: Boolean(item.isRequired),
              isArchived: Boolean(item.isArchived),
              order: Number(item.order) || 0,
              fieldName: String(item.fieldName),
              showInFilters: Boolean(item.showInFilters),
              isCopyable: Boolean(item.isCopyable),
              showInStatistics: Boolean(item.showInStatistics),
              showOnDashboard: Boolean(item.showOnDashboard)
            };
          })
        };
      });

      // Create a clean document structure
      const documentData = {
        groups: processedCategories,
        metadata: {
          lastModified: new Date().toISOString(),
          modifiedBy: user.email,
          version: 1
        }
      };

      console.log('Prepared document data:', documentData);

      // Save to Firestore using updateDoc instead of setDoc
      const categoriesRef = doc(db, 'categories', 'active_categories');
      
      // First try to get the document
      const docSnap = await getDoc(categoriesRef);
      
      if (!docSnap.exists()) {
        // If document doesn't exist, create it
        await setDoc(categoriesRef, documentData);
      } else {
        // If document exists, update it
        await updateDoc(categoriesRef, {
          groups: processedCategories,
          'metadata.lastModified': new Date().toISOString(),
          'metadata.modifiedBy': user.email,
          'metadata.version': docSnap.data().metadata?.version ? docSnap.data().metadata.version + 1 : 1
        });
      }

      console.log('Save operation completed successfully');

      setSnackbar({
        open: true,
        message: 'Categories saved successfully',
        severity: 'success',
      });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Detailed error saving categories:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to save categories: ';
      if (error.code === 'permission-denied') {
        errorMessage += 'You do not have permission to perform this action.';
      } else if (error.code === 'unauthenticated') {
        errorMessage += 'Please log in and try again.';
      } else {
        errorMessage += error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveCategories(categories);
      setHasUnsavedChanges(false);
      setSnackbar({
        open: true,
        message: 'Categories saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      setSnackbar({
        open: true,
        message: `Error saving changes: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const saveGroupTitle = async (groupId) => {
    if (!editedGroupTitle) return;

    // Check for duplicate names
    if (isDuplicateName(editedGroupTitle, 'group', groupId)) {
      setSnackbar({
        open: true,
        message: 'A group with this name already exists',
        severity: 'error'
      });
      return;
    }

    const newCategories = categories.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          title: editedGroupTitle
        };
      }
      return group;
    });

    setCategories(newCategories);
    setEditingGroup(null);
    setEditedGroupTitle('');
    setHasUnsavedChanges(true);
  };

  const saveItemTitle = async (groupId, itemId) => {
    if (!editedItemTitle) return;

    // Check for duplicate names
    if (isDuplicateName(editedItemTitle, 'item', itemId)) {
      setSnackbar({
        open: true,
        message: 'A category with this name already exists',
        severity: 'error'
      });
      return;
    }

    try {
      setSaving(true);

      // Get the current item to access its old fieldName and ID
      const currentItem = categories
        .find(group => group.id === groupId)
        ?.items.find(item => item.id === itemId);

      if (!currentItem) {
        throw new Error('Category not found');
      }

      // Generate new field identifiers
      const newFieldName = normalizeId(editedItemTitle).replace('category_', '');
      const newId = `category_${newFieldName}`;
      
      // Check if only the data type is changing (title remains the same)
      const isTitleUnchanged = currentItem.title === editedItemTitle;
      const isTypeChanged = currentItem.type !== editedItemType;
      const isOnlyTypeChanging = isTitleUnchanged && isTypeChanged;

      // Update categories with new identifiers
      const newCategories = categories.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            items: group.items.map(item => 
              item.id === itemId 
                ? { 
                    ...item, 
                    title: editedItemTitle,
                    type: editedItemType,
                    fieldName: newFieldName,
                    id: newId
                  }
                : item
            )
          };
        }
        return group;
      });

      // Get hotels collection to update field names
      const hotelsRef = collection(db, 'hotels');
      const hotelsSnapshot = await getDocs(hotelsRef);
      const batch = writeBatch(db);

      // Update all hotels to use the new field name
      hotelsSnapshot.forEach((doc) => {
        const hotelData = doc.data();
        const oldCategoryKey = `category_${currentItem.fieldName}`;
        const newCategoryKey = `category_${newFieldName}`;
        
        if (hotelData.categoryData && hotelData.categoryData[oldCategoryKey]) {
          const updatedHotel = {
            ...hotelData,
            categoryData: {
              ...hotelData.categoryData,
              [newCategoryKey]: {
                // Keep existing data but update the timestamp to reflect the type change
                ...hotelData.categoryData[oldCategoryKey],
                updatedAt: isTypeChanged ? new Date() : hotelData.categoryData[oldCategoryKey].updatedAt,
                updatedBy: isTypeChanged ? user.email : hotelData.categoryData[oldCategoryKey].updatedBy
              }
            }
          };
          
          // Only remove the old category key if the field name actually changed
          if (oldCategoryKey !== newCategoryKey) {
            delete updatedHotel.categoryData[oldCategoryKey];
          }
          
          batch.update(doc.ref, updatedHotel);
        }
      });

      // Save categories
      const categoriesRef = doc(db, 'categories', 'active_categories');
      batch.set(categoriesRef, { 
        groups: newCategories,
        metadata: {
          lastModified: new Date(),
          modifiedBy: user.email,
          version: 1
        }
      }, { merge: true });

      // Commit all changes
      await batch.commit();

      setCategories(newCategories);
      setEditingItem(null);
      setEditedItemTitle('');
      setEditedItemType('text');
      setHasUnsavedChanges(true);
      
      const message = isOnlyTypeChanging 
        ? 'Category type updated successfully (data preserved)'
        : 'Category updated successfully';
        
      setSnackbar({
        open: true,
        message,
        severity: 'success'
      });

    } catch (error) {
      console.error('Error saving category:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save category: ' + error.message,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const moveGroup = (currentIndex, direction) => {
    const newCategories = [...categories];
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= newCategories.length) return;

    // Swap groups
    [newCategories[currentIndex], newCategories[newIndex]] = [newCategories[newIndex], newCategories[currentIndex]];

    // Update order values
    newCategories.forEach((group, index) => {
      group.order = index;
    });

    setCategories(newCategories);
    setHasUnsavedChanges(true);
    setSnackbar({
      open: true,
      message: 'Changes made. Remember to save!',
      severity: 'info'
    });
  };

  const handleFeatureToggle = (itemId, groupId, feature) => {
    const newCategories = categories.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          items: g.items.map(i => {
            if (i.id === itemId) {
              return {
                ...i,
                [feature]: !i[feature]
              };
            }
            return i;
          })
        };
      }
      return g;
    });
    setCategories(newCategories);
    setHasUnsavedChanges(true);
  };

  // Render logic
  if (user?.role !== 'admin') {
    return (
      <Box p={3}>
        <Typography color="error">
          You don't have permission to access this feature.
        </Typography>
      </Box>
    );
  }

  // Display error if present
  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      position: 'relative', 
      minHeight: '100%',
      maxWidth: '1400px',
      margin: '0 auto',
      px: { xs: 2, md: 4 },
      pt: { xs: 2, md: 4 },
      pb: 0,
      bgcolor: 'background.default',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 4
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton 
                onClick={() => navigate('/')} 
                sx={{
                  color: 'text.secondary',
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                  },
                  borderRadius: 2
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontFamily: 'StrawberrySansDisplay',
                    fontWeight: 400,
                    mb: 1,
                    color: 'text.primary',
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    letterSpacing: '-0.02em'
                  }}
                >
                  Category Management
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ 
                    opacity: 0.8,
                    fontFamily: 'StrawberrySansDisplay'
                  }}
                >
                  Organize and manage your hotel information categories
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setBulkUpdateOpen(true)}
                startIcon={<UploadFileIcon />}
                sx={{ borderRadius: 2 }}
              >
                Bulk Update
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCsvImportOpen(true)}
                startIcon={<FileUploadIcon />}
                sx={{ borderRadius: 2 }}
              >
                Import from CSV
              </Button>
              <Tooltip title="Keyboard Shortcuts">
                <IconButton
                  onClick={() => setShortcutsDialogOpen(true)}
                  sx={{
                    color: 'text.secondary',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                    '&:hover': {
                      color: 'primary.main',
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                    },
                    borderRadius: 2
                  }}
                >
                  <KeyboardIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 350px' },
            gap: 4,
            flex: 1,
            minHeight: 0,
            overflow: 'auto'
          }}>
            {/* Main Content */}
            <Box sx={{ 
              flex: 1,
              overflow: 'auto',
              pr: 2,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme => alpha(theme.palette.primary.main, 0.1),
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: theme => alpha(theme.palette.primary.main, 0.2),
              },
            }}>
              {/* Add New Group */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: theme => alpha(theme.palette.primary.main, 0.1)
                }}
              >
                <TextField
                  size="small"
                  placeholder="New group name..."
                  value={newGroupTitle}
                  onChange={(e) => setNewGroupTitle(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newGroupTitle) {
                      addGroup();
                    }
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={addGroup}
                  disabled={!newGroupTitle}
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: 'none'
                  }}
                >
                  Add Group
                </Button>
              </Paper>

              {/* Top Warning Banner */}
              {hasUnsavedChanges && (
                <Fade in={hasUnsavedChanges}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 3,
                      bgcolor: alpha('#FFA726', 0.1),
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: '1px solid',
                      borderColor: alpha('#FFA726', 0.2),
                    }}
                  >
                    <Typography sx={{ color: 'warning.dark', fontWeight: 500 }}>
                      You have unsaved changes
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                      sx={{
                        bgcolor: 'warning.main',
                        '&:hover': {
                          bgcolor: 'warning.dark',
                        },
                        borderRadius: 2,
                        textTransform: 'none',
                        px: 3
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Paper>
                </Fade>
              )}

              {/* Category Groups */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {categories.map((group, index) => (
                  <Paper
                    key={group.id}
                    elevation={0}
                    sx={{ 
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                        boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.05)}`
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* Group Header */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 2,
                      pb: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        mr: 1
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => moveGroup(index, 'up')}
                          disabled={index === 0}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'primary.main',
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                            }
                          }}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveGroup(index, 'down')}
                          disabled={index === categories.length - 1}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'primary.main',
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                            }
                          }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <IconButton
                        onClick={() => toggleGroup(group.id)}
                        size="small"
                        sx={{
                          mr: 1,
                          color: 'primary.main',
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                          '&:hover': {
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                          }
                        }}
                      >
                        {expandedGroups[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      
                      {editingGroup === group.id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <TextField
                            size="small"
                            value={editedGroupTitle}
                            onChange={(e) => setEditedGroupTitle(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                saveGroupTitle(group.id);
                              }
                            }}
                            autoFocus
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => saveGroupTitle(group.id)}
                            sx={{
                              color: 'primary.main',
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                              '&:hover': {
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                              }
                            }}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setEditingGroup(null)}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'error.main',
                                bgcolor: theme => alpha(theme.palette.error.main, 0.04)
                              }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              flex: 1,
                              fontWeight: 600,
                              fontSize: '1.1rem'
                            }}
                          >
                            {group.title}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingGroup(group.id);
                              setEditedGroupTitle(group.title);
                            }}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'primary.main',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => deleteGroup(group.id)}
                            disabled={group.items.some(item => !item.isArchived)}
                            sx={{
                              color: 'error.main',
                              opacity: group.items.some(item => !item.isArchived) ? 0.5 : 1,
                              '&:hover': { 
                                color: 'error.dark',
                                bgcolor: theme => alpha(theme.palette.error.main, 0.04)
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    {/* Group Items */}
                    <Collapse in={expandedGroups[group.id]}>
                      <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                      }}>
                        {/* Add Category Form */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: theme => alpha(theme.palette.primary.main, 0.1)
                          }}
                        >
                          <TextField
                            size="small"
                            placeholder="New category name..."
                            value={selectedGroup === group.id ? newItemTitle : ''}
                            onChange={(e) => {
                              setSelectedGroup(group.id);
                              setNewItemTitle(e.target.value);
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && newItemTitle) {
                                addItem();
                              }
                            }}
                            sx={{
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                              }
                            }}
                          />
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={newItemType}
                              onChange={(e) => setNewItemType(e.target.value)}
                              displayEmpty
                              sx={{
                                borderRadius: 2,
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: alpha('#000', 0.1)
                                }
                              }}
                            >
                              {dataTypes.map(type => (
                                <MenuItem key={type.value} value={type.value}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <type.icon fontSize="small" />
                                    {type.label}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="contained"
                            onClick={() => {
                              setSelectedGroup(group.id);
                              addItem();
                            }}
                            disabled={!newItemTitle || selectedGroup !== group.id}
                            startIcon={<AddIcon />}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              boxShadow: 'none'
                            }}
                          >
                            Add
                          </Button>
                        </Paper>

                        {group.items
                          .filter(item => !item.isArchived)
                          .map((item, index) => (
                            <Paper
                              key={item.id}
                              elevation={0}
                              sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                                  transform: 'translateX(4px)',
                                  boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.05)}`
                                }
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 0.5 
                              }}>
                                <IconButton
                                  size="small"
                                  onClick={() => moveItem(group.id, index, 'up')}
                                  disabled={index === 0}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                      color: 'primary.main',
                                      bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                                    }
                                  }}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => moveItem(group.id, index, 'down')}
                                  disabled={index === group.items.filter(i => !i.isArchived).length - 1}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                      color: 'primary.main',
                                      bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                                    }
                                  }}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              
                              {editingItem === item.id ? (
                                <Box sx={{ flex: 1 }}>
                                  <Alert 
                                    severity="warning" 
                                    sx={{ 
                                      mb: 2,
                                      py: 0.5,
                                      '& .MuiAlert-message': {
                                        fontSize: '0.813rem'
                                      }
                                    }}
                                  >
                                    <strong>Important:</strong> Click the green checkmark button to save changes and preserve all hotel data. Do NOT use the main Save button at the top.
                                  </Alert>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2
                                  }}>
                                  <TextField
                                    size="small"
                                    value={editedItemTitle}
                                    onChange={(e) => setEditedItemTitle(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        saveItemTitle(group.id, item.id);
                                      }
                                    }}
                                    autoFocus
                                    fullWidth
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2
                                      }
                                    }}
                                  />
                                  <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select
                                      value={editedItemType}
                                      onChange={(e) => setEditedItemType(e.target.value)}
                                      displayEmpty
                                      sx={{
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: alpha('#000', 0.1)
                                        }
                                      }}
                                    >
                                      {dataTypes.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <type.icon fontSize="small" />
                                            {type.label}
                                          </Box>
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <Tooltip title="Save changes and preserve all hotel data" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => saveItemTitle(group.id, item.id)}
                                      sx={{
                                        color: 'success.main',
                                        bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                                        border: '2px solid',
                                        borderColor: 'success.main',
                                        '&:hover': {
                                          bgcolor: theme => alpha(theme.palette.success.main, 0.2)
                                        }
                                      }}
                                    >
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingItem(null);
                                      setEditedItemTitle('');
                                      setEditedItemType('text');
                                    }}
                                    sx={{
                                      color: 'text.secondary',
                                      '&:hover': {
                                        color: 'error.main',
                                        bgcolor: theme => alpha(theme.palette.error.main, 0.04)
                                      }
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                  </Box>
                                </Box>
                              ) : (
                                <>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 500 }}>
                                      {item.title}
                                    </Typography>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary" 
                                      sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.5,
                                        mt: 0.5,
                                        opacity: 0.8
                                      }}
                                    >
                                      {(() => {
                                        const TypeIcon = dataTypes.find(t => t.value === item.type)?.icon || TextFieldsIcon;
                                        return (
                                          <>
                                            <TypeIcon fontSize="small" />
                                            {dataTypes.find(t => t.value === item.type)?.label || 'Text'}
                                          </>
                                        );
                                      })()}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => setFeatureMenuAnchor({ element: e.currentTarget, item, group })}
                                      sx={{
                                        color: 'text.secondary',
                                        bgcolor: (theme) => 
                                          (item.showInFilters || item.isCopyable || item.showInStatistics || item.isRequired) 
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : 'transparent',
                                        '&:hover': {
                                          bgcolor: theme => alpha(theme.palette.primary.main, 0.15)
                                        },
                                        borderRadius: 1.5,
                                        position: 'relative',
                                        '&::after': {
                                          content: '""',
                                          position: 'absolute',
                                          top: 2,
                                          right: 2,
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          bgcolor: item.isRequired ? 'error.main' : 'primary.main',
                                          opacity: (item.showInFilters || item.isCopyable || item.showInStatistics || item.isRequired) ? 1 : 0,
                                          transition: 'opacity 0.2s ease'
                                        }
                                      }}
                                    >
                                      <SettingsIcon fontSize="small" />
                                    </IconButton>

                                    <FormControl size="small">
                                      <Select
                                        value=""
                                        displayEmpty
                                        onChange={(e) => moveItemToGroup(group.id, e.target.value, index)}
                                        sx={{
                                          '& .MuiSelect-select': {
                                            py: 0.5,
                                            px: 1.5,
                                            color: 'text.secondary',
                                            borderRadius: 2
                                          },
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: alpha('#000', 0.1)
                                          }
                                        }}
                                      >
                                        <MenuItem value="" disabled>Move to...</MenuItem>
                                        {categories
                                          .filter(g => g.id !== group.id)
                                          .map(g => (
                                            <MenuItem key={g.id} value={g.id}>{g.title}</MenuItem>
                                          ))
                                        }
                                      </Select>
                                    </FormControl>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditingItem(item.id);
                                        setEditedItemTitle(item.title);
                                        setEditedItemType(item.type || 'text');
                                      }}
                                      sx={{
                                        color: 'text.secondary',
                                        '&:hover': {
                                          color: 'primary.main',
                                          bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                                        }
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => deleteItem(group.id, item.id)}
                                      sx={{
                                        color: 'error.main',
                                        '&:hover': { 
                                          color: 'error.dark',
                                          bgcolor: theme => alpha(theme.palette.error.main, 0.04)
                                        }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </>
                              )}
                            </Paper>
                          ))}
                      </Box>
                    </Collapse>
                  </Paper>
                ))}
              </Box>

              {/* Keyboard Shortcuts Dialog */}
              <Dialog
                open={shortcutsDialogOpen}
                onClose={() => setShortcutsDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                  elevation: 0,
                  sx: {
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }
                }}
              >
                <DialogTitle sx={{ pb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Keyboard Shortcuts
                  </Typography>
                </DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                        General
                      </Typography>
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'auto 1fr', 
                        gap: 2, 
                        alignItems: 'center' 
                      }}>
                        <Typography 
                          variant="mono" 
                          sx={{ 
                            bgcolor: alpha('#000', 0.04), 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontSize: '0.875rem'
                          }}
                        >
                          Ctrl + S
                        </Typography>
                        <Typography color="text.secondary">Save changes</Typography>
                        <Typography 
                          variant="mono" 
                          sx={{ 
                            bgcolor: alpha('#000', 0.04), 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontSize: '0.875rem'
                          }}
                        >
                          Esc
                        </Typography>
                        <Typography color="text.secondary">Cancel editing</Typography>
                        <Typography 
                          variant="mono" 
                          sx={{ 
                            bgcolor: alpha('#000', 0.04), 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontSize: '0.875rem'
                          }}
                        >
                          Enter
                        </Typography>
                        <Typography color="text.secondary">Confirm changes</Typography>
                      </Box>
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 2 }}>
                  <Button 
                    onClick={() => setShortcutsDialogOpen(false)}
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3
                    }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Confirmation Dialog */}
              <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }
                }}
              >
                <DialogTitle sx={{ pb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Confirm Changes
                  </Typography>
                </DialogTitle>
                <DialogContent>
                  <DialogContentText sx={{ color: 'text.secondary' }}>
                    The following changes will be applied:
                  </DialogContentText>
                  {confirmAction && (
                    <Box sx={{ mt: 2 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          color: 'warning.main',
                          bgcolor: alpha('#FFA726', 0.1),
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: alpha('#FFA726', 0.2)
                        }}
                      >
                        {confirmAction.message}
                      </Typography>
                    </Box>
                  )}
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 2 }}>
                  <Button 
                    onClick={() => setConfirmDialogOpen(false)}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: alpha('#000', 0.04)
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleConfirmAction} 
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3
                    }}
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Snackbar for notifications */}
              <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              >
                <Alert
                  onClose={handleCloseSnackbar}
                  severity={snackbar.severity}
                  sx={{ 
                    width: '100%',
                    borderRadius: 2,
                    alignItems: 'center'
                  }}
                >
                  {snackbar.message}
                </Alert>
              </Snackbar>
            </Box>

            {/* Preview Section */}
            <Box sx={{ 
              display: { xs: 'none', lg: 'block' },
              borderLeft: '1px solid',
              borderColor: 'divider',
              pl: 4,
              height: '100%',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme => alpha(theme.palette.primary.main, 0.1),
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: theme => alpha(theme.palette.primary.main, 0.2),
              },
            }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 3,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}
              >
                Live Preview
              </Typography>
              <CategoryPreview categories={categories} />
            </Box>
          </Box>

          {/* Add the CategoryBulkUpdate dialog */}
          <CategoryBulkUpdate 
            categories={categories}
            open={bulkUpdateOpen}
            onClose={() => setBulkUpdateOpen(false)}
          />

                        {/* Add the CategoryCSVImport dialog */}
          <CategoryCSVImport 
            categories={categories}
            open={csvImportOpen}
            onClose={() => setCsvImportOpen(false)}
          />

          {/* Feature Menu - Single instance outside the map */}
          <Menu
            anchorEl={featureMenuAnchor?.element}
            open={Boolean(featureMenuAnchor)}
            onClose={() => setFeatureMenuAnchor(null)}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 220,
                boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                '& .MuiList-root': {
                  p: 1
                }
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
            }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 0.5
                }}
              >
                Category Features
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block'
                }}
              >
                Configure visibility and functionality
              </Typography>
            </Box>

            <Box sx={{ p: 1 }}>
              <MenuItem
                onClick={() => {
                  handleFeatureToggle(
                    featureMenuAnchor?.item.id,
                    featureMenuAnchor?.group.id,
                    'isRequired'
                  );
                  setFeatureMenuAnchor(null);
                }}
                sx={{
                  borderRadius: 1,
                  gap: 1.5,
                  py: 1,
                  px: 2,
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Box sx={{ 
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: theme => featureMenuAnchor?.item.isRequired 
                    ? alpha(theme.palette.error.main, 0.12)
                    : alpha(theme.palette.action.hover, 0.04),
                  color: featureMenuAnchor?.item.isRequired ? 'error.main' : 'text.secondary'
                }}>
                  <StarIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    Required field
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Must be filled when adding hotels
                  </Typography>
                </Box>
                {featureMenuAnchor?.item.isRequired && (
                  <CheckIcon fontSize="small" color="error" sx={{ ml: 1 }} />
                )}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleFeatureToggle(
                    featureMenuAnchor?.item.id,
                    featureMenuAnchor?.group.id,
                    'showInFilters'
                  );
                  setFeatureMenuAnchor(null);
                }}
                sx={{
                  borderRadius: 1,
                  gap: 1.5,
                  py: 1,
                  px: 2,
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Box sx={{ 
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: theme => featureMenuAnchor?.item.showInFilters 
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.action.hover, 0.04),
                  color: featureMenuAnchor?.item.showInFilters ? 'primary.main' : 'text.secondary'
                }}>
                  <FilterListIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    Show in filters
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Display as a filterable field
                  </Typography>
                </Box>
                {featureMenuAnchor?.item.showInFilters && (
                  <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                )}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleFeatureToggle(
                    featureMenuAnchor?.item.id,
                    featureMenuAnchor?.group.id,
                    'isCopyable'
                  );
                  setFeatureMenuAnchor(null);
                }}
                sx={{
                  borderRadius: 1,
                  gap: 1.5,
                  py: 1,
                  px: 2,
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Box sx={{ 
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: theme => featureMenuAnchor?.item.isCopyable 
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.action.hover, 0.04),
                  color: featureMenuAnchor?.item.isCopyable ? 'primary.main' : 'text.secondary'
                }}>
                  <ContentCopyIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    Copyable
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enable copy to clipboard
                  </Typography>
                </Box>
                {featureMenuAnchor?.item.isCopyable && (
                  <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                )}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleFeatureToggle(
                    featureMenuAnchor?.item.id,
                    featureMenuAnchor?.group.id,
                    'showInStatistics'
                  );
                  setFeatureMenuAnchor(null);
                }}
                sx={{
                  borderRadius: 1,
                  gap: 1.5,
                  py: 1,
                  px: 2,
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Box sx={{ 
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: theme => featureMenuAnchor?.item.showInStatistics 
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.action.hover, 0.04),
                  color: featureMenuAnchor?.item.showInStatistics ? 'primary.main' : 'text.secondary'
                }}>
                  <BarChartIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    Show in statistics
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include in data analysis
                  </Typography>
                </Box>
                {featureMenuAnchor?.item.showInStatistics && (
                  <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                )}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleFeatureToggle(
                    featureMenuAnchor?.item.id,
                    featureMenuAnchor?.group.id,
                    'showOnDashboard'
                  );
                  setFeatureMenuAnchor(null);
                }}
                sx={{
                  borderRadius: 1,
                  gap: 1.5,
                  py: 1,
                  px: 2,
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Box sx={{ 
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: theme => featureMenuAnchor?.item.showOnDashboard 
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.action.hover, 0.04),
                  color: featureMenuAnchor?.item.showOnDashboard ? 'primary.main' : 'text.secondary'
                }}>
                  <BarChartIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    Show on Dashboard
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Display as a card on dashboard
                  </Typography>
                </Box>
                {featureMenuAnchor?.item.showOnDashboard && (
                  <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                )}
              </MenuItem>
            </Box>
          </Menu>
        </>
      )}
    </Box>
  );
};

export default CategoryManager; 